import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { advisorService } from '../../services/advisorService';
import { dashboardService } from '../../services/dashboardService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import NetworkTreeNode from './components/NetworkTreeNode';
import NetworkStatistics from './components/NetworkStatistics';
import ReferralLinkGenerator from './components/ReferralLinkGenerator';
import NetworkFilters from './components/NetworkFilters';
import TopPerformersPanel from './components/TopPerformersPanel';
import RecruitmentTrendsChart from './components/RecruitmentTrendsChart';
import NetworkDetailModal from './components/NetworkDetailModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ReferralNetwork = () => {
  const { user, userProfile } = useAuth();
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState('tree');
  const [networkData, setNetworkData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    performanceLevel: 'all',
    activityStatus: 'all',
    registrationPeriod: 'all',
    searchQuery: ''
  });

  useEffect(() => {
    if (user?.id) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      
      const [referralTree, statsData, referralLink] = await Promise.all([
        advisorService?.buildReferralTree(user?.id, 5),
        advisorService?.getAdvisorStats(user?.id),
        dashboardService?.getReferralLink(user?.id)
      ]);
      
      setStats(statsData);
      
      // Build root node with user data and recursive children
      const rootNode = {
        id: 'root',
        name: userProfile?.full_name || 'Usuario',
        avatar: userProfile?.avatar_url || null,
        avatarAlt: 'Retrato profesional de asesor',
        registrationDate: new Date(userProfile?.created_at || Date.now())?.toLocaleDateString('es-PE'),
        isActive: true,
        totalReferrals: statsData?.totalReferrals || 0,
        monthlySales: 0,
        monthlyCommission: statsData?.monthlyEarnings || 0,
        totalCommission: statsData?.totalEarnings || 0,
        level: 1,
        email: userProfile?.email || '',
        phone: userProfile?.phone || '',
        location: 'Arequipa, Perú',
        referralCode: referralLink?.referralCode || '',
        recentActivity: [
          { icon: 'UserPlus', description: 'Nuevo referido registrado', date: new Date()?.toLocaleDateString('es-PE') },
          { icon: 'ShoppingCart', description: 'Venta completada', date: new Date()?.toLocaleDateString('es-PE') },
          { icon: 'Award', description: 'Alcanzó nivel Top Performer', date: new Date()?.toLocaleDateString('es-PE') }
        ],
        children: referralTree
      };
      
      setNetworkData(rootNode);
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet?.has(nodeId)) {
        newSet?.delete(nodeId);
      } else {
        newSet?.add(nodeId);
      }
      return newSet;
    });
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleCloseModal = () => {
    setSelectedNode(null);
  };

  const breadcrumbItems = [
  { label: 'Inicio', path: '/landing-page' },
  { label: 'Red de Referidos', path: '/referral-network' }];


  const networkStats = {
    totalMembers: stats?.totalReferrals || 0,
    activeMembers: stats?.activeReferrals || 0,
    totalCommissions: stats?.totalEarnings || 0,
    monthlyGrowth: 12.5,
    averagePerformance: 85,
    topPerformerName: userProfile?.full_name || 'Usuario'
  };

  const topPerformers = [
  {
    id: 1,
    name: userProfile?.full_name || 'Usuario',
    avatar: userProfile?.avatar_url || "https://img.rocket.new/generatedImages/rocket_gen_img_16be51f71-1763295001188.png",
    avatarAlt: 'Retrato profesional de asesor',
    totalReferrals: stats?.totalReferrals || 0,
    monthlySales: 8,
    monthlyCommission: stats?.monthlyEarnings || 0,
    performanceScore: 98,
    trend: 'up'
  }];


  const recruitmentTrends = [
  { month: 'Ago', count: 8 },
  { month: 'Sep', count: 12 },
  { month: 'Oct', count: 15 },
  { month: 'Nov', count: 18 },
  { month: 'Dic', count: 22 },
  { month: 'Ene', count: stats?.totalReferrals || 0 }];


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando red de referidos...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />

        {/* Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Red de Referidos
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                Visualiza y gestiona tu red de referidos multinivel
              </p>
            </div>

            {/* Action Buttons - Mobile optimized */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant={viewMode === 'tree' ? 'default' : 'outline'}
                iconName="GitBranch"
                onClick={() => setViewMode('tree')}
                className="text-xs sm:text-sm"
              >
                Vista Árbol
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                iconName="List"
                onClick={() => setViewMode('list')}
                className="text-xs sm:text-sm"
              >
                Vista Lista
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                onClick={() => console.log('Export network')}
                className="text-xs sm:text-sm"
              >
                Exportar
              </Button>
            </div>
          </div>
        </div>

        {/* Network Statistics - Mobile optimized */}
        <div className="mb-4 sm:mb-6">
          <NetworkStatistics statistics={networkStats} />
        </div>

        {/* Main Content Grid - Mobile stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Left Sidebar - Filters & Link Generator - Full width on mobile */}
          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            <div className="card p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                <Icon name="Filter" size={18} className="mr-2 text-primary" />
                Filtros
              </h2>
              <NetworkFilters filters={filters} onFilterChange={setFilters} />
            </div>

            <div className="card p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                <Icon name="Link" size={18} className="mr-2 text-primary" />
                Link de Referido
              </h2>
              <ReferralLinkGenerator userId={user?.id} referralCode={networkData?.referralCode || ''} />
            </div>
          </div>

          {/* Main Network View - Full width on mobile */}
          <div className="lg:col-span-6">
            <div className="card p-4 sm:p-5 lg:p-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-4 sm:mb-6 flex items-center">
                <Icon name="Network" size={20} className="mr-2 text-primary" />
                {viewMode === 'tree' ? 'Vista de Árbol' : 'Vista de Lista'}
              </h2>
              {viewMode === 'tree' && networkData && (
                <div className="overflow-x-auto">
                  <NetworkTreeNode
                    node={networkData}
                    isExpanded={expandedNodes?.has('root')}
                    onToggle={() => toggleNode('root')}
                    onToggleExpand={() => toggleNode('root')}
                    onNodeClick={handleNodeClick}
                    expandedNodes={expandedNodes}
                    level={0}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Top Performers & Trends - Full width on mobile */}
          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            <div className="card p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                <Icon name="Trophy" size={18} className="mr-2 text-primary" />
                Top Performers
              </h2>
              <TopPerformersPanel performers={topPerformers} />
            </div>

            <div className="card p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                <Icon name="TrendingUp" size={18} className="mr-2 text-primary" />
                Tendencias
              </h2>
              <RecruitmentTrendsChart data={recruitmentTrends} />
            </div>
          </div>
        </div>
      </div>

      {/* Network Detail Modal */}
      {selectedNode && (
        <NetworkDetailModal node={selectedNode} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default ReferralNetwork;